import { InitializeFileUploadSchema, TeamFile } from '@enview/interface/interface';
import FileResult from '@enview/interface/types/FileResult';
import { BillSearchCriteria } from '@enview/interface/types/BillSearch';
import axios from 'axios';
import instance from '../config/axiosConfig';

export const deleteFile = async (fileId: string): Promise<any> => {
  return instance
    .delete(`/files/${fileId}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error('Error deleting user files');
      return error;
    });
};

export const uploadFile = async (
  file: any,
  sourceFeature: string,
  teams: number[],
): Promise<TeamFile> => {
  const contentType = file.mediaType || file.type || 'text/plain';
  const data = {
    contentType,
    fileName: file.name,
    fileSizeBytes: file.size,
    teamIds: teams,
    sourceFeature,
  };

  try {
    const initializeUploadResponse = await instance.post<InitializeFileUploadSchema>(
      '/files',
      data,
    );
    const { userFile, signedUrl } = initializeUploadResponse.data;
    await axios.put(signedUrl, file, {
      headers: { 'Content-Type': contentType },
    });
    return userFile;
  } catch (error) {
    console.log('Error saving user files', error);
    throw error;
  }
};

export const downloadFile = async (fileId: string): Promise<any> => {
  return instance
    .get(`/files/${fileId}/download`)
    .then((response) => {
      window.open(response.data.fileUrl);
    })
    .catch((error) => {
      console.error('Error with file download', error);
    });
};

export const downloadExportFile = async (
  teamIds: number[],
  properties: string[],
  tagIds?: number[],
): Promise<void> => {
  const data = { teamIds, tagIds, properties, exportFormat: 'csv' };
  return instance
    .post('/tracked-bills/export', data, {
      headers: {
        'x-utc-offset': new Date().getTimezoneOffset(),
      },
      responseType: 'blob',
      timeout: 90000,
    })
    .then((response) => {
      const fileName = FileResult.getFileName(response.headers['content-disposition']);
      const blob = new Blob([response.data]);
      downloadFileBlob(blob, fileName);
    });
};

// All array fields on BillSearchCriteria that may contain null/undefined entries
// when built from URL query params (e.g. ArrayParam yields [undefined] for absent params).
const CRITERIA_ARRAY_KEYS: (keyof BillSearchCriteria)[] = [
  'statuses',
  'jurisdictions',
  'committees',
  'sessions',
  'sessionNames',
  'chambers',
  'billNumbers',
  'tagIds',
  'trackingTeamsScopeFilter',
  'trackingTeamsFilter',
  'legislativeTypes',
  'insightsPolicyTopics',
  'partySponsorFilters',
];

// Strips null/undefined items from every array field before the criteria is sent
// to the export endpoint. The backend Joi schema rejects non-string array items,
// so [undefined] (produced by buildParamsFromQuery when a param is absent) would
// fail validation without this step.
const sanitizeCriteria = (criteria: BillSearchCriteria): BillSearchCriteria => {
  const result = { ...criteria } as Record<string, unknown>;
  for (const key of CRITERIA_ARRAY_KEYS) {
    const val = result[key];
    if (Array.isArray(val)) {
      const filtered = val.filter((v) => v != null);
      result[key] = filtered.length > 0 ? filtered : undefined;
    }
  }
  return result as unknown as BillSearchCriteria;
};

export const downloadExportByCriteria = async (
  criteria: BillSearchCriteria,
  properties: string[],
): Promise<void> => {
  const data = {
    criteria: sanitizeCriteria(criteria),
    properties,
    exportFormat: 'csv',
  };
  return instance
    .post('/bills/export', data, {
      headers: {
        'x-utc-offset': new Date().getTimezoneOffset(),
      },
      responseType: 'blob',
      timeout: 180000,
    })
    .then((response) => {
      const fileName = FileResult.getFileName(response.headers['content-disposition']);
      const blob = new Blob([response.data]);
      downloadFileBlob(blob, fileName);
    });
};

export const downloadFileBlob = (blob: Blob, fileName: string): void => {
  // Create an anchor element and dispatch a click event to download file
  const a = document.createElement('a');
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  const clickEvt = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};
